<template>
  <div>
    <IconButton
      v-if="!title"
      v-tooltip="$t('common.back')"
      :icon="icon ?? 'arrow_back'"
      :color="iconButtonColor"
      @click="tryToNavigateBack"
    />
    <BaseTextButton v-else color="text" :icon="icon ?? 'arrow_back'" @click="tryToNavigateBack">{{
      title
    }}</BaseTextButton>
    <Dialog
      :open="showNavigateBackDialog"
      class="fixed inset-0 z-40 overflow-y-auto"
      @close="closeNavigateBackDialog"
    >
      <div class="flex min-h-screen items-center justify-center">
        <DialogOverlay class="fixed inset-0 bg-black opacity-30" />
        <div class="rounded-extra-large z-10 mx-auto max-w-lg border border-gray-100 bg-white p-8">
          <DialogTitle class="mb-4 text-2xl font-medium">
            {{ $t('app.unsaved-changes.title') }}
          </DialogTitle>
          <DialogDescription class="mb-4">
            {{ $t('app.unsaved-changes.description') }}
          </DialogDescription>
          <div class="mt-8 flex flex-row justify-end gap-x-1">
            <BaseTextButton color="filled" warning @click="navigateBack">{{
              $t('app.unsaved-changes.cancel')
            }}</BaseTextButton>
            <BaseTextButton
              color="filled"
              :loading="loadingState == ApiRequestStatus.Loading"
              @click="onSave"
              >{{ $t('common.save') }}</BaseTextButton
            >
          </div>
        </div>
      </div>
    </Dialog>
  </div>
</template>
<script setup lang="ts">
  import { ref, watch } from 'vue'
  import { Dialog, DialogOverlay, DialogTitle, DialogDescription } from '@headlessui/vue'
  import { useRouter, type LocationQueryRaw, type RouteParamsRaw } from 'vue-router'
  import { useI18n } from 'vue-i18n'
  import IconButton from './base/button/icon-button/BaseIconButton.vue'
  import BaseTextButton from './base/button/text-button/BaseTextButton.vue'
  import { useOnBeforeUnload } from '../composables/useBeforeUnload'
  import { ApiRequestStatus } from '../models/api/api-request-status'
  import type { BaseIconButtonType } from './base/button/icon-button/base-icon-button-type.ts'

  interface Props {
    icon?: string
    title?: string
    backupRouteName: string
    params?: RouteParamsRaw
    query?: LocationQueryRaw
    loadingState?: ApiRequestStatus
    unsavedChanges?: boolean
    onSave?: () => void
    close?: boolean
    iconButtonColor?: BaseIconButtonType
  }
  const props = defineProps<Props>()
  const unsavedChangesRef = ref(false)

  const router = useRouter()
  const { t } = useI18n()

  const hasPreviousPage = (): boolean => {
    return !!router.options.history.state.back
  }

  watch(
    () => props.unsavedChanges,
    (updatedValue) => {
      unsavedChangesRef.value = updatedValue ?? false
    },
  )

  const navigateBack = (): void => {
    if (!props.close && hasPreviousPage()) {
      router.back()
    } else {
      router.replace({ name: props.backupRouteName, params: props.params, query: props.query })
    }
  }

  useOnBeforeUnload(t('app.unsaved-changes.leave'), ref(unsavedChangesRef))

  const showNavigateBackDialog = ref(false)

  const tryToNavigateBack = () => {
    if (props.unsavedChanges) {
      showNavigateBackDialog.value = true
    } else {
      navigateBack()
    }
  }

  const closeNavigateBackDialog = () => (showNavigateBackDialog.value = false)
</script>
